/**
 * Kitchen display hooks.
 *
 * Two things here that differ from every other module.
 *
 * The rail arrives over a WebSocket rather than by polling: a docket that shows
 * up fifteen seconds late is food plated fifteen seconds late, all night.
 *
 * Elapsed time is recomputed in the browser between refetches. It is the only
 * number this client calculates, and only because a clock that ticks once a
 * second cannot come from the server — the *authoritative* elapsed time is
 * still whatever the API last said, and every refetch resets to it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import { useApi } from './provider';

export type TicketUrgency = 'on_time' | 'approaching' | 'late';
export type TicketState = 'queued' | 'fired' | 'bumped';

export interface TicketItem {
  id: string;
  order_line_id: string;
  name: string;
  qty: string;
  seat_no: number | null;
  note: string | null;
  modifiers: string[];
}

export interface Ticket {
  id: string;
  station_id: string;
  order_id: string;
  order_number: number;
  table_label: string | null;
  course_no: number;
  /**
   * Dishes added to this course after the station had bumped the rest of it.
   * A docket of their own, so they are seen at all, and marked, so a lone
   * side is not plated as if it were the whole course.
   */
  follow_up: boolean;
  /** The note on the whole tab ("birthday — candle with dessert"). */
  order_note: string | null;
  state: TicketState;
  fired_at: string | null;
  bumped_at: string | null;
  elapsed_seconds: number;
  /**
   * Null on a plan without `kds.sla` — no prep target, so no colour and no
   * breach. Not zero: zero would mean a target of nought and paint every
   * docket late. Elapsed time is always there; how long a docket has been up
   * is not the paid feature, judging it against a promise is.
   */
  sla_seconds: number | null;
  urgency: TicketUrgency | null;
  sla_breached: boolean | null;
  items: TicketItem[];
  /**
   * The site the order was taken at, when another site's kitchen is cooking
   * it — a central kitchen making a branch's desserts. Null for this site's
   * own orders. The docket says so, or table 12 across town goes out to
   * table 12 here.
   */
  from_location_id: string | null;
  from_location_name: string | null;
}

export interface Station {
  id: string;
  location_id: string;
  name: string;
  /** "prep" cooks what is routed to it; "expo" watches whole orders; "printer" prints. */
  kind: StationKind;
  /** Null on a plan without `kds.sla`. See `Ticket.sla_seconds`. */
  sla_seconds: number | null;
  is_default: boolean;
  open_tickets: number;
}

export function useStations(locationId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['kds', 'stations', locationId ?? null],
    queryFn: ({ signal }) =>
      api.get<Station[]>(`/v1/kds/stations?location_id=${locationId}`, signal),
    enabled: Boolean(locationId),
    staleTime: 60_000,
  });
}

/**
 * Put a kitchen screen on the books. Counts against `kds.screens.max`.
 *
 * `isDefault` is where a line lands when no routing rule matches, and a
 * location has exactly one — a kitchen with no default silently drops food.
 * So the first station at a site should be it.
 */
export function useCreateStation() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    Station,
    ApiError,
    {
      locationId: string;
      name: string;
      kind?: StationKind;
      slaSeconds?: number;
      isDefault?: boolean;
    }
  >({
    mutationFn: (input) =>
      api.post<Station>('/v1/kds/stations', {
        location_id: input.locationId,
        name: input.name,
        kind: input.kind ?? 'prep',
        sla_seconds: input.slaSeconds ?? null,
        is_default: input.isDefault ?? false,
      }),
    onSettled: () => {
      // The rail's own station picker reads this key, so a new screen shows
      // up in the kitchen without anybody reloading a tablet.
      void queryClient.invalidateQueries({ queryKey: ['kds', 'stations'] });
    },
  });
}

/** What a rule matches on, most specific first — the order the server applies them. */
export type MatchType = 'item' | 'category' | 'prep_station' | 'any';

export interface RoutingRule {
  id: string;
  /** The site whose orders the rule routes. */
  location_id: string;
  location_name: string;
  station_id: string;
  station_name: string;
  /**
   * Where the screen is. Differs from `location_id` only for a rule sending
   * food to another site's kitchen (`kds.cross_location`).
   */
  station_location_id: string;
  station_location_name: string;
  match_type: MatchType;
  /** An item or category id, a prep-station name, or null for `any`. */
  match_value: string | null;
  /**
   * The item's or category's name. Null for `any` — and for a rule whose item
   * or category no longer exists, which never fires and should read as broken
   * rather than as blank.
   */
  match_name: string | null;
  priority: number;
  /**
   * Other screens this rule ties with — same kind, same target, same priority.
   *
   * How a kitchen says "two grills, either will do". With
   * `kds.load_balancing` an order goes to whichever of them is quieter; without
   * it, always to the first. Empty for a rule with no rival, which is nearly
   * all of them.
   */
  shares_with: string[];
}

/**
 * Where each dish goes, most specific rule first.
 *
 * An item rule beats a category rule for that item, which beats a prep-station
 * rule, which beats `any`; then higher priority wins. That order is the answer
 * to "why did the fish go to Grill", so a screen should keep it.
 */
export function useRoutingRules(locationId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['kds', 'routing-rules', locationId ?? null],
    queryFn: ({ signal }) =>
      api.get<RoutingRule[]>(`/v1/kds/routing-rules?location_id=${locationId}`, signal),
    enabled: Boolean(locationId),
    staleTime: 60_000,
  });
}

/**
 * Other sites' rules sending food to this site's screens — what a central
 * kitchen is cooking for whom. Read-only: a rule is the ordering site's.
 */
export function useIncomingRoutingRules(locationId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['kds', 'routing-rules', 'incoming', locationId ?? null],
    queryFn: ({ signal }) =>
      api.get<RoutingRule[]>(
        `/v1/kds/routing-rules/incoming?location_id=${locationId}`,
        signal,
      ),
    enabled: Boolean(locationId),
    staleTime: 60_000,
  });
}

/**
 * Send a dish, a category, a prep station or everything to a screen.
 *
 * The screen can be at another site with `kds.cross_location` (402 without
 * it), as long as it is not that site's pass (409 `station_id`). Refused too:
 * an item or category that does not exist (404), a `match_value` that is not
 * an id for those types (409 `match_value`), and a site the person cannot
 * reach, at either end (403).
 */
export function useCreateRoutingRule() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    { id: string },
    ApiError,
    {
      locationId: string;
      stationId: string;
      matchType: MatchType;
      matchValue?: string;
      priority?: number;
    }
  >({
    mutationFn: (input) =>
      api.post<{ id: string }>('/v1/kds/routing-rules', {
        location_id: input.locationId,
        station_id: input.stationId,
        match_type: input.matchType,
        match_value: input.matchValue ?? null,
        priority: input.priority ?? 0,
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['kds', 'routing-rules'] });
    },
  });
}

export function useDeleteRoutingRule() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { ruleId: string }>({
    mutationFn: ({ ruleId }) =>
      api.request<void>(`/v1/kds/routing-rules/${ruleId}`, { method: 'DELETE' }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['kds', 'routing-rules'] });
    },
  });
}

/**
 * Rename a screen, change its ticket target, or make it the default.
 *
 * There is no un-defaulting: the default can only be handed to another
 * station, because a site with none drops any dish no rule matches. A target
 * needs `kds.sla` and answers 402 without it.
 */
export function useUpdateStation() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    Station,
    ApiError,
    {
      stationId: string;
      name?: string;
      kind?: StationKind;
      slaSeconds?: number;
      makeDefault?: true;
    }
  >({
    mutationFn: ({ stationId, name, kind, slaSeconds, makeDefault }) =>
      api.request<Station>(`/v1/kds/stations/${stationId}`, {
        method: 'PATCH',
        body: {
          ...(name === undefined ? {} : { name }),
          ...(kind === undefined ? {} : { kind }),
          ...(slaSeconds === undefined ? {} : { sla_seconds: slaSeconds }),
          ...(makeDefault ? { make_default: true } : {}),
        },
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['kds'] });
    },
  });
}

export function useRail(stationId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['kds', 'rail', stationId],
    queryFn: ({ signal }) => api.get<Ticket[]>(`/v1/kds/stations/${stationId}/tickets`, signal),
    enabled: Boolean(stationId),
    // The socket drives updates. This is the safety net for a dropped
    // connection, not the primary path — a kitchen screen that silently stops
    // updating is worse than one that refetches too often.
    refetchInterval: 30_000,
  });
}

/**
 * What a station is. `printer` is routed like `prep` — same rules, same
 * default — and its tickets come out on paper instead of onto a screen.
 */
export type StationKind = 'prep' | 'expo' | 'printer';

/**
 * One station's docket for a course. A station can appear twice: what it
 * cooked for the course, and dishes added after it bumped that.
 */
export interface PassStation {
  ticket_id: string;
  station_id: string;
  station_name: string;
  follow_up: boolean;
  state: TicketState;
  bumped_at: string | null;
  items: TicketItem[];
  /** The site whose kitchen cooks this share, when it is not this one. */
  kitchen_name: string | null;
}

/** A whole course for one table, gathered from every station cooking it. */
export interface PassDocket {
  order_id: string;
  order_number: number;
  table_label: string | null;
  course_no: number;
  /** The note on the whole tab. */
  order_note: string | null;
  /** The earliest station's clock: the pass waits on the whole course. */
  fired_at: string | null;
  elapsed_seconds: number;
  sla_seconds: number | null;
  urgency: TicketUrgency | null;
  /** Every station cooking it has bumped. What the pass is waiting for. */
  ready: boolean;
  stations: PassStation[];
  /** The site the order was taken at, when it is not this one. */
  from_location_name: string | null;
}

/**
 * What the pass sees: courses across every station, not one screen's lines.
 *
 * A course stays here until it is called away rather than until it is ready —
 * dropping it when the last station bumped would take it off the screen at the
 * moment somebody standing there needs it.
 */
export function usePassRail(stationId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['kds', 'pass', stationId],
    queryFn: ({ signal }) =>
      api.get<PassDocket[]>(`/v1/kds/stations/${stationId}/pass`, signal),
    enabled: Boolean(stationId),
    // The pass has no socket of its own: it is assembled from several
    // stations' dockets, and any of them changing changes it. Five seconds is
    // the slowest this can be and still beat somebody walking over to look.
    refetchInterval: 5_000,
  });
}

/** The food has left the kitchen. Clears the course from every rail. */
export function useCallAway() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    { cleared: number },
    ApiError,
    { stationId: string; orderId: string; courseNo: number }
  >({
    mutationFn: ({ stationId, orderId, courseNo }) =>
      api.post<{ cleared: number }>(`/v1/kds/stations/${stationId}/away`, {
        order_id: orderId,
        course_no: courseNo,
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['kds'] });
    },
  });
}

export interface CourseReadiness {
  order_id: string;
  course_no: number;
  /** Every station cooking this course has bumped it. */
  ready: boolean;
  /** When the last of them finished. Null until they all have. */
  ready_at: string | null;
  /** How many stations have a docket for it, and how many are done. */
  stations: number;
  stations_ready: number;
}

/**
 * Whether the food is up, for the tabs on screen.
 *
 * Asked by tab rather than by site: bumped dockets stay after a tab closes, so
 * a site-wide answer would grow all evening. Poll rather than push — the
 * kitchen's socket is per station, and a waiter finding out ten seconds late
 * is the difference between walking to the pass and not.
 */
export function useReadiness(orderIds: string[]) {
  const api = useApi();
  const ids = [...orderIds].sort();
  return useQuery({
    queryKey: ['kds', 'readiness', ids],
    queryFn: ({ signal }) =>
      api.get<CourseReadiness[]>(
        `/v1/kds/readiness?${ids.map((id) => `order_id=${id}`).join('&')}`,
        signal,
      ),
    enabled: ids.length > 0,
    refetchInterval: 15_000,
  });
}

export function useBumpTicket() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Ticket, ApiError, { ticketId: string }>({
    mutationFn: ({ ticketId }) => api.post<Ticket>(`/v1/kds/tickets/${ticketId}/bump`),
    // Optimistic: a bump bar is hit with the side of a hand, and a docket that
    // lingers half a second gets hit again.
    onMutate: async ({ ticketId }) => {
      await queryClient.cancelQueries({ queryKey: ['kds', 'rail'] });
      const snapshots = queryClient.getQueriesData<Ticket[]>({ queryKey: ['kds', 'rail'] });
      for (const [key, rail] of snapshots) {
        if (!rail) continue;
        queryClient.setQueryData<Ticket[]>(
          key,
          rail.filter((ticket) => ticket.id !== ticketId),
        );
      }
      return { snapshots };
    },
    onError: (_error, _vars, context) => {
      const ctx = context as
        { snapshots?: [readonly unknown[], Ticket[] | undefined][] } | undefined;
      for (const [key, rail] of ctx?.snapshots ?? []) {
        queryClient.setQueryData(key, rail);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['kds', 'rail'] });
    },
  });
}

export function useRecallTicket() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Ticket, ApiError, { ticketId: string }>({
    mutationFn: ({ ticketId }) => api.post<Ticket>(`/v1/kds/tickets/${ticketId}/recall`),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['kds', 'rail'] });
    },
  });
}

export type StreamStatus = 'connecting' | 'live' | 'offline';

/**
 * Subscribe to a station's rail.
 *
 * The socket carries nudges, not state — every message triggers a refetch, so
 * one code path draws the screen whether the update arrived over the wire or
 * from a reload. A dropped message costs a render, not correctness.
 *
 * Reconnects with backoff and reports its own status, because a kitchen screen
 * that has quietly stopped listening looks exactly like a quiet kitchen.
 */
export function useRailStream(stationId: string | null): StreamStatus {
  const api = useApi();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<StreamStatus>('connecting');
  const attemptRef = useRef(0);

  const refetch = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['kds', 'rail', stationId] });
  }, [queryClient, stationId]);

  useEffect(() => {
    if (!stationId) return;

    let socket: WebSocket | null = null;
    let retryTimer: number | undefined;
    let cancelled = false;

    const connect = async () => {
      if (cancelled) return;
      // Asked for rather than read: a wall screen can hold this socket open for
      // a whole service without making one HTTP request, so its access token
      // would otherwise be the only one in the building nothing ever renews.
      const token = await api.accessToken();
      if (cancelled) return;
      if (!token) {
        setStatus('offline');
        return;
      }

      const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const url =
        `${scheme}://${window.location.host}/v1/kds/stations/${stationId}` +
        `/stream?token=${encodeURIComponent(token)}`;

      setStatus(attemptRef.current === 0 ? 'connecting' : 'offline');
      socket = new WebSocket(url);

      socket.onopen = () => {
        attemptRef.current = 0;
        setStatus('live');
        // Catch up on anything missed while disconnected.
        refetch();
      };
      socket.onmessage = () => refetch();
      socket.onerror = () => setStatus('offline');
      socket.onclose = () => {
        if (cancelled) return;
        setStatus('offline');
        // Backoff, capped: a kitchen screen left on overnight must not hammer
        // a restarting API, and must reconnect promptly when it returns.
        const delay = Math.min(1000 * 2 ** attemptRef.current, 15_000);
        attemptRef.current += 1;
        retryTimer = window.setTimeout(() => void connect(), delay);
      };
    };

    void connect();

    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
      socket?.close();
    };
  }, [api, stationId, refetch]);

  return status;
}

/**
 * A clock that ticks locally between refetches.
 *
 * The server's `elapsed_seconds` is authoritative and resets this on every
 * fetch; this only fills the gap so the number on the wall moves.
 */
export function useTicking(seconds: number): number {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    setOffset(0);
    const id = window.setInterval(() => setOffset((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [seconds]);

  return seconds + offset;
}

/** "8:05". Minutes and seconds, because a kitchen counts in both. */
export function formatElapsed(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safe / 60);
  // Past an hour the seconds are noise and "306:21" reads as a number rather
  // than as five hours: a docket that old is a forgotten one.
  if (minutes >= 60)
    return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
  const rest = safe % 60;
  return `${minutes}:${String(rest).padStart(2, '0')}`;
}

/**
 * Recompute urgency locally from the ticking clock.
 *
 * Mirrors the server's thresholds deliberately: the alternative is a docket
 * that stays green for thirty seconds after it should have gone amber, which
 * defeats the point of the colour.
 */
export function urgencyAt(
  elapsedSeconds: number,
  slaSeconds: number | null,
): TicketUrgency | null {
  // No target, no judgement. Returning 'on_time' here would be a claim, and
  // the wrong one at twenty minutes — the rail should simply carry no colour.
  if (slaSeconds === null) return null;
  if (slaSeconds <= 0) return 'on_time';
  if (elapsedSeconds >= slaSeconds) return 'late';
  if (elapsedSeconds >= slaSeconds * 0.75) return 'approaching';
  return 'on_time';
}

export interface PrintItem {
  id: string;
  name: string;
  qty: string;
  seat_no: number | null;
  note: string | null;
  modifiers: string[];
  /** Taken off the order after it printed. Print it as a void. */
  voided: boolean;
}

/** One slip of paper: a ticket's dishes that have not been printed yet. */
export interface PrintJob {
  ticket_id: string;
  order_id: string;
  order_number: number;
  table_label: string | null;
  course_no: number;
  fired_at: string;
  /** An addition or a void to a ticket already in the kitchen. */
  follow_up: boolean;
  /** The note on the whole tab, as it stood when the slip was read. */
  order_note: string | null;
  items: PrintItem[];
  /** The site the order is for, when this kitchen cooks for another. */
  from_location_name: string | null;
}

/**
 * What a printer station still has to print, oldest first.
 *
 * Keyed under the station's rail, so the socket that nudges a screen when a
 * ticket lands nudges the printer too; the interval is the fallback for a
 * dropped socket, and short, because a ticket that prints a minute late is a
 * table waiting a minute longer.
 */
export function usePrintQueue(stationId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['kds', 'rail', stationId, 'print-queue'],
    queryFn: ({ signal }) =>
      api.get<PrintJob[]>(`/v1/kds/stations/${stationId}/print-queue`, signal),
    enabled: Boolean(stationId),
    refetchInterval: 5_000,
  });
}

/**
 * Tell the server these dishes are on paper.
 *
 * The dishes, not the ticket: one can join the ticket between reading the
 * queue and printing it, and stamping the ticket would lose it. Safe to send
 * twice.
 */
export function useMarkPrinted() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<{ stamped: number }, ApiError, { ticketId: string; itemIds: string[] }>({
    mutationFn: ({ ticketId, itemIds }) =>
      api.post<{ stamped: number }>(`/v1/kds/tickets/${ticketId}/printed`, {
        item_ids: itemIds,
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['kds', 'rail'] });
      void queryClient.invalidateQueries({ queryKey: ['kds', 'stations'] });
    },
  });
}

/** Tickets done, and how long they took from sent to bumped. */
export interface Pace {
  tickets: number;
  /** Tickets with a time. A docket rebuilt after a reboot has none. */
  timed: number;
  /** The typical ticket. Null when nothing was timed. */
  median_seconds: number | null;
  /** One ticket in ten took at least this long. */
  slow_seconds: number | null;
  /** Bumped after the station's target had passed. */
  late: number;
}

export interface StationPace {
  station_id: string;
  station_name: string;
  pace: Pace;
  /** Dishes, not dockets. A decimal string, like every quantity. */
  items: string;
  recalls: number;
  /** Tickets cooked here for orders taken at another site. */
  for_other_sites: number;
}

export interface HourPace {
  /** 0–23 on the site's clock. */
  hour: number;
  pace: Pace;
}

export interface DayPace {
  business_date: string;
  pace: Pace;
}

export interface SitePace {
  location_id: string;
  location_name: string;
  tickets: number;
}

export interface KitchenPace {
  location_id: string;
  from_date: string;
  to_date: string;
  station_id: string | null;
  pace: Pace;
  items: string;
  recalls: number;
  stations: StationPace[];
  /** Only the hours anything was bumped in. */
  hours: HourPace[];
  days: DayPace[];
  for_other_sites: SitePace[];
}

/**
 * How fast a site's kitchen turned tickets round (`kds.analytics`, 402
 * without it). Business days on the site's clock; without dates, today. With
 * a station, only that station — what a kitchen screen shows in its corner.
 */
export function useKitchenPace(
  input: {
    locationId: string | undefined;
    fromDate?: string;
    toDate?: string;
    stationId?: string | null;
  },
  enabled = true,
) {
  const api = useApi();
  const params = new URLSearchParams();
  if (input.locationId) params.set('location_id', input.locationId);
  if (input.fromDate) params.set('from_date', input.fromDate);
  if (input.toDate) params.set('to_date', input.toDate);
  if (input.stationId) params.set('station_id', input.stationId);
  return useQuery({
    queryKey: [
      'kds',
      'pace',
      input.locationId ?? null,
      input.fromDate ?? null,
      input.toDate ?? null,
      input.stationId ?? null,
    ],
    queryFn: ({ signal }) =>
      api.get<KitchenPace>(`/v1/kds/analytics?${params.toString()}`, signal),
    enabled: enabled && Boolean(input.locationId),
    // Figures, not the rail: a minute old is fine, and a wall screen showing
    // them should not add a request every few seconds.
    staleTime: 60_000,
    refetchInterval: 120_000,
  });
}
